/**
 * User list badges (Phase 5): license expiry and invite status.
 */
import { INVITE_PENDING_STATUS, UserData } from '../../dtos/admin/user.dto';

export const LICENSE_WARNING_DAYS = 30;

export interface UserBadge {
  label: string;
  className: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function dayNumber(ymd: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY_MS;
}

/**
 * "License expired" once the expiry date has passed, "Expires in N days" within 30 days
 * (today counts as 0 days), otherwise null.
 * @param today the agency's current date, YYYY-MM-DD
 */
export function getLicenseBadge(licenseExpiry: string | null | undefined, today: string): UserBadge | null {
  if (!licenseExpiry) return null;
  const expiry = dayNumber(licenseExpiry);
  const now = dayNumber(today);
  if (expiry === null || now === null) return null;
  const days = expiry - now;
  if (days < 0) {
    return { label: 'License expired', className: 'bg-red-100 text-red-800 border border-red-200' };
  }
  if (days <= LICENSE_WARNING_DAYS) {
    const label = days === 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`;
    return { label, className: 'bg-amber-100 text-amber-800 border border-amber-200' };
  }
  return null;
}

export function isInvitePending(user: Pick<UserData, 'cognitoStatus' | 'invitePending'>): boolean {
  return user.invitePending === true || user.cognitoStatus === INVITE_PENDING_STATUS;
}

export const INVITE_PENDING_BADGE: UserBadge = {
  label: 'Invite pending',
  className: 'bg-sky-100 text-sky-800 border border-sky-200',
};

/** A revoked invite (backend status Revoked): no edit, resend or revoke. */
export function isRevoked(user: Pick<UserData, 'status'>): boolean {
  return (user.status || '').toUpperCase() === 'REVOKED';
}

export const REVOKED_BADGE: UserBadge = {
  label: 'Revoked',
  className: 'bg-gray-100 text-gray-700 border border-gray-300',
};
